import * as Sentry from '@sentry/vue';

/**
 * Axios network failures (no HTTP response: the connection dropped, the device
 * went offline, or the request was blocked) are environmental and are already
 * handled by the response interceptor in bootstrap.js -- it retries safe
 * requests, drives the offline connectivity gate, and only then re-throws. The
 * throw is intentional (callers must see the rejection), but reporting it to
 * Sentry again is pure noise, so drop those events here.
 *
 * Scoped narrowly -- an Axios error, with no response, carrying code
 * 'ERR_NETWORK' -- so unrelated exceptions that merely share that code or a
 * similar message are never suppressed.
 */
function isHandledNetworkError(error) {
    if (!error || typeof error !== 'object') {
        return false;
    }

    const isAxiosError = error.isAxiosError === true || error.name === 'AxiosError';

    return isAxiosError && error.response == null && error.code === 'ERR_NETWORK';
}

export default function setupErrorReporting(app) {
    try {
        const props = JSON.parse(document.getElementById('app')?.dataset.page).props;

        const userId = props?.auth?.user?.id;
        const dsn = props?.sentry.dsn;
        const release = props?.sentry.release;

        if (!dsn) {
            return;
        }

        if (userId) {
            Sentry.setUser({ id: userId });
        }

        Sentry.init({
            app,
            dsn,
            integrations: [Sentry.replayIntegration()],
            tracesSampleRate: 0.1,
            tracePropagationTargets: [],
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            release: release,
            beforeSend(event, hint) {
                if (isHandledNetworkError(hint?.originalException)) {
                    return null;
                }

                return event;
            },
        });
    } catch {}
}
